/**
 * Create support desk ticket (SON-###### assigned server-side).
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import apiService from '../services/api';
import { isMspAgreementServiceType } from '../utils/mspAgreement';

type OptionItem = { value: string; label: string; sort_order: number };

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  accountId?: string;
}

type ServiceRow = {
  id: string;
  service_id: string;
  service_type: string;
  service_type_name?: string | null;
};

const SupportDeskTicketFormDialog: React.FC<Props> = ({ open, onClose, onSuccess, accountId }) => {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<{ id: string; display_name: string }[]>([]);
  const [users, setUsers] = useState<{ id: string; username: string; display_name: string }[]>([]);
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [mspBalanceHint, setMspBalanceHint] = useState<string | null>(null);
  const [opts, setOpts] = useState<{
    status: OptionItem[];
    priority: OptionItem[];
    category: OptionItem[];
    channel: OptionItem[];
  }>({ status: [], priority: [], category: [], channel: [] });
  const [form, setForm] = useState({
    account_id: accountId || '',
    subject: '',
    description: '',
    category: 'general',
    status: 'new',
    priority: 'medium',
    channel: 'staff',
    assignee_id: '',
    requester_email: '',
    requester_name: '',
    account_service_id: '',
    msp_use_hours: false,
    msp_hours_input: '',
  });

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({
      ...f,
      account_id: accountId || f.account_id,
      account_service_id: '',
      msp_use_hours: false,
      msp_hours_input: '',
    }));
    apiService.getAccounts({ page: 1, per_page: 500 }).then((r) =>
      setAccounts((r.accounts || []).map((a: any) => ({
        id: a.id,
        display_name: a.display_name || a.organisation_name || a.id,
      })))
    ).catch(() => {});
    apiService.getUsers().then(setUsers).catch(() => {});
    apiService.getSupportDeskFieldOptionsGrouped().then((g) => {
      setOpts({
        status: g.status || [],
        priority: g.priority || [],
        category: g.category || [],
        channel: g.channel || [],
      });
    }).catch(() => {});
  }, [open, accountId]);

  useEffect(() => {
    if (!form.account_id) {
      setServices([]);
      return;
    }
    apiService.getAccountServices(form.account_id).then((r: any) => {
      setServices(r.services || r || []);
    }).catch(() => setServices([]));
  }, [form.account_id]);

  useEffect(() => {
    if (!form.account_service_id) {
      setMspBalanceHint(null);
      return;
    }
    const s = services.find((x) => x.id === form.account_service_id);
    if (!s || !isMspAgreementServiceType(s.service_type, s.service_type_name)) {
      setMspBalanceHint(null);
      return;
    }
    let cancelled = false;
    apiService
      .getMspBalance(s.id)
      .then((b: any) => {
        if (!cancelled) {
          setMspBalanceHint(
            `MSP balance — available: ${b.available_total} h (granted ${b.total_grants}, reserved ${b.reserved_total})`
          );
        }
      })
      .catch(() => {
        if (!cancelled) setMspBalanceHint(null);
      });
    return () => {
      cancelled = true;
    };
  }, [form.account_service_id, services]);

  const submit = async () => {
    if (!form.account_id?.trim() || !form.subject.trim()) {
      setErr('Account and subject are required');
      return;
    }
    if (form.msp_use_hours) {
      const h = parseFloat(form.msp_hours_input || '0');
      if (!form.account_service_id || Number.isNaN(h) || h <= 0) {
        setErr('Select a related MSP service and enter hours greater than zero');
        return;
      }
    }
    setSaving(true);
    setErr(null);
    try {
      await apiService.createSupportDeskTicket({
        account_id: form.account_id,
        subject: form.subject.trim(),
        description: form.description || undefined,
        category: form.category || undefined,
        status: form.status,
        priority: form.priority,
        channel: form.channel,
        assignee_id: form.assignee_id || undefined,
        requester_email: form.requester_email || undefined,
        requester_name: form.requester_name || undefined,
        account_service_id: form.account_service_id || undefined,
        msp_use_hours: !!form.msp_use_hours,
        msp_hours: form.msp_use_hours ? parseFloat(form.msp_hours_input || '0') : undefined,
      });
      onSuccess();
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSaving(false);
    }
  };

  const catOpts = opts.category.length ? opts.category : [{ value: 'general', label: 'General', sort_order: 0 }];
  const stOpts = opts.status.length ? opts.status : [{ value: 'new', label: 'New', sort_order: 0 }];
  const priOpts = opts.priority.length ? opts.priority : [{ value: 'medium', label: 'Medium', sort_order: 0 }];
  const chOpts = opts.channel.length ? opts.channel : [{ value: 'staff', label: 'Staff', sort_order: 0 }];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New support desk ticket</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Autocomplete
          options={accounts}
          getOptionLabel={(o) => o.display_name}
          value={accounts.find((a) => a.id === form.account_id) || null}
          onChange={(_, v) =>
            setForm({
              ...form,
              account_id: v?.id || '',
              account_service_id: '',
              msp_use_hours: false,
              msp_hours_input: '',
            })
          }
          renderInput={(params) => <TextField {...params} label="Account" margin="normal" fullWidth required />}
          sx={{ mt: 1 }}
          disabled={!!accountId}
        />
        <TextField label="Subject" fullWidth margin="normal" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        <TextField label="Description" fullWidth margin="normal" multiline minRows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        {services.length > 0 && (
          <FormControl fullWidth margin="normal">
            <InputLabel>Related service</InputLabel>
            <Select
              value={form.account_service_id || ''}
              label="Related service"
              onChange={(e) =>
                setForm({
                  ...form,
                  account_service_id: e.target.value || '',
                  msp_use_hours: false,
                  msp_hours_input: '',
                })
              }
            >
              <MenuItem value="">None</MenuItem>
              {services.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.service_id} ({s.service_type})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {form.account_service_id &&
          (() => {
            const s = services.find((x) => x.id === form.account_service_id);
            if (!s || !isMspAgreementServiceType(s.service_type, s.service_type_name)) return null;
            return (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, my: 1 }}>
                {mspBalanceHint && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {mspBalanceHint}
                  </Typography>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!form.msp_use_hours}
                      onChange={(e) => setForm({ ...form, msp_use_hours: e.target.checked })}
                    />
                  }
                  label="Use MSP agreement hours for this ticket"
                />
                {form.msp_use_hours && (
                  <TextField
                    label="Hours to allocate"
                    type="number"
                    inputProps={{ step: 0.25, min: 0 }}
                    value={form.msp_hours_input || ''}
                    onChange={(e) => setForm({ ...form, msp_hours_input: e.target.value })}
                    fullWidth
                    sx={{ mt: 1 }}
                    required
                  />
                )}
              </Box>
            );
          })()}
        <FormControl fullWidth margin="normal">
          <InputLabel>Category</InputLabel>
          <Select value={form.category} label="Category" onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {catOpts.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Status</InputLabel>
          <Select value={form.status} label="Status" onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {stOpts.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Priority</InputLabel>
          <Select value={form.priority} label="Priority" onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            {priOpts.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Channel</InputLabel>
          <Select value={form.channel} label="Channel" onChange={(e) => setForm({ ...form, channel: e.target.value })}>
            {chOpts.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Assignee</InputLabel>
          <Select value={form.assignee_id} label="Assignee" onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}>
            <MenuItem value="">(none)</MenuItem>
            {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.display_name || u.username}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label="Requester email" fullWidth margin="normal" value={form.requester_email} onChange={(e) => setForm({ ...form, requester_email: e.target.value })} />
        <TextField label="Requester name" fullWidth margin="normal" value={form.requester_name} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>{saving ? <CircularProgress size={22} /> : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupportDeskTicketFormDialog;
